import React from "react";
import { Modal, InputNumber, Input } from "antd";
import PropTypes from "prop-types";

/**
 * EditCreditModal - Modal for editing wallet credit
 * Supports two modes: "adjust" (add/subtract) and "direct" (set exact value)
 */
const EditCreditModal = ({
  visible,
  record,
  mode,
  credit,
  adjustment,
  note,
  loading,
  onModeChange,
  onCreditChange,
  onAdjustmentChange,
  onNoteChange,
  onCancel,
  onSubmit,
}) => {
  if (!record) return null;

  const currentCredit = record.credit_available || 0;
  const estimatedResult =
    mode === "adjust" ? currentCredit + (adjustment || 0) : credit || 0;

  // Validation: require adjustment/credit value and note
  const isFormValid =
    mode === "adjust"
      ? adjustment !== null && adjustment !== undefined && note.trim() !== ""
      : credit !== null && credit !== undefined && note.trim() !== "";

  const showResultPreview =
    (mode === "adjust" && adjustment !== null && adjustment !== undefined) ||
    (mode === "direct" && credit !== null && credit !== undefined);

  return (
    <Modal
      title="Edit Credit"
      visible={visible}
      onCancel={onCancel}
      onOk={onSubmit}
      confirmLoading={loading}
      okText="Save"
      okButtonProps={{ disabled: !isFormValid }}
      maskClosable={false}
    >
      {/* Current Credit */}
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 8,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>
          CURRENT CREDIT
        </div>
        <div style={{ fontSize: 24, fontWeight: 600 }}>
          {currentCredit.toLocaleString()}{" "}
          <span style={{ fontSize: 14, fontWeight: 400 }}>credits</span>
        </div>
      </div>

      {/* Mode Toggle */}
      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => onModeChange("adjust")}
          style={{
            flex: 1,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 500,
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            background: mode === "adjust" ? "#1890ff" : "#f0f0f0",
            color: mode === "adjust" ? "#fff" : "#666",
            transition: "all 0.2s",
          }}
        >
          Adjust
        </button>
        <button
          type="button"
          onClick={() => onModeChange("direct")}
          style={{
            flex: 1,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 500,
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            background: mode === "direct" ? "#faad14" : "#f0f0f0",
            color: mode === "direct" ? "#fff" : "#666",
            transition: "all 0.2s",
          }}
        >
          Direct Set
        </button>
      </div>

      {/* Adjust Mode */}
      {mode === "adjust" && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{ marginBottom: 6, color: "#333", fontSize: 13, fontWeight: 500 }}
          >
            Adjustment Amount
          </div>
          <div style={{ marginBottom: 6, color: "#999", fontSize: 11 }}>
            ใส่ค่าบวกเพื่อเพิ่ม หรือค่าลบเพื่อหัก
          </div>
          <InputNumber
            style={{ width: "100%" }}
            value={adjustment}
            onChange={onAdjustmentChange}
            precision={4}
            max={1000000}
            min={-1000000}
            placeholder="e.g. 1000 or -500"
          />
        </div>
      )}

      {/* Direct Mode */}
      {mode === "direct" && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{ marginBottom: 6, color: "#333", fontSize: 13, fontWeight: 500 }}
          >
            New Credit Value
          </div>
          <div
            style={{
              marginBottom: 8,
              padding: 8,
              background: "#fff7e6",
              borderRadius: 4,
              fontSize: 11,
              color: "#d46b08",
              lineHeight: 1.4,
            }}
          >
            หากผู้ใช้มีการใช้งาน credit ระหว่างนี้ ยอดที่ตั้งอาจไม่ตรงกับความเป็นจริง
            แนะนำให้ใช้ Adjust แทน
          </div>
          <InputNumber
            style={{ width: "100%" }}
            value={credit}
            onChange={onCreditChange}
            min={0}
            max={1000000}
            precision={4}
            placeholder="Enter new credit value"
          />
        </div>
      )}

      {/* Result Preview */}
      {showResultPreview && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: estimatedResult >= 0 ? "#f6ffed" : "#fff2f0",
            borderRadius: 6,
            borderLeft: "3px solid " + (estimatedResult >= 0 ? "#52c41a" : "#ff4d4f"),
          }}
        >
          <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
            NEW CREDIT
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: estimatedResult >= 0 ? "#52c41a" : "#ff4d4f",
            }}
          >
            {(estimatedResult || 0).toLocaleString()} credits
          </div>
        </div>
      )}

      {/* Note */}
      <div>
        <div
          style={{ marginBottom: 6, color: "#333", fontSize: 13, fontWeight: 500 }}
        >
          Note <span style={{ color: '#ff4d4f' }}>*</span>
        </div>
        <Input.TextArea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Reason for this change..."
          rows={2}
          style={{ borderRadius: 6 }}
        />
      </div>
    </Modal>
  );
};

EditCreditModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  record: PropTypes.object,
  mode: PropTypes.oneOf(["adjust", "direct"]),
  credit: PropTypes.number,
  adjustment: PropTypes.number,
  note: PropTypes.string,
  loading: PropTypes.bool,
  onModeChange: PropTypes.func.isRequired,
  onCreditChange: PropTypes.func.isRequired,
  onAdjustmentChange: PropTypes.func.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

EditCreditModal.defaultProps = {
  record: null,
  mode: "adjust",
  credit: null,
  adjustment: null,
  note: "",
  loading: false,
};

export default EditCreditModal;



// WEBPACK FOOTER //
// ./src/containers/User/UserManagement/components/MemberCreditWallet/modals/EditCreditModal.js